@let modulePinned = userSettingsStore.panelClassPinned$ | async;
@let moduleMain = userSettingsStore.panelClassMain$ | async;
<div class="conference-panel">
  <!-- Recording consent (native modal, top layer). Mounted outside the
       contentReady gate so it never waits on the 240ms subtree delay. -->
  @if (odinService.recordingConsentPromptVisible()) {
    <andor-recording-consent-dialog
      [startedByName]="recordingOwnerName()"
      (consented)="odinService.grantRecordingConsent()"
      (dismissed)="odinService.dismissRecordingConsent()" />
  }
  <div class="main-area {{ modulePinned ? 'is-pinned' : '' }} {{ sidePanelClass() }}">
    @if (contentReady()) {
      @if ((appService.activeBreakpoint$ | async) !== 'small') {
        @if (modulePinned) {
          <div class="pinned fade-in">
            <andor-components-frame
              [component]="modulePinned"
              mode="pinned"></andor-components-frame>
          </div>
        }
      }
      @if (moduleMain) {
        <div class="main fade-in">
          <andor-panel-nav-bar />
          <andor-components-frame [component]="moduleMain" mode="main"></andor-components-frame>
        </div>
      }
    } @else {
      <div class="main">
        <div class="content-loading">
          <div class="spinner" aria-hidden="true"></div>
        </div>
      </div>
    }
  </div>

  <div class="right-side-panel {{ sidePanelClass() }}">
    <div class="user-list-wrapper">
      @if (contentReady()) {
        <andor-users-list class="fade-in"></andor-users-list>
      }
    </div>

    <div class="actions-wrapper">
      @let tab = activeTab();
      <div
        #actionsElement
        class="actions"
        [class.expanded]="isSideBarActionsMenuOpen"
        [style.height.px]="actionsHeight">
        <div #actionsContent class="actions-content" [class.visible]="isContentVisible">
          @if (tab) {
            <div class="actions-header">
              <h3 class="actions-header-title">
                @switch (tab) {
                  @case ('breakout') {
                    {{ 'Breakout Room' | translate }}
                  }
                  @case ('activity') {
                    {{ 'Activity Log' | translate }}
                  }
                  @case ('transcription') {
                    {{ 'AI Transcription' | translate }}
                  }
                }
              </h3>
              @if (tab === 'activity') {
                <div class="actions-header-actions">
                  <andor-square-icon-btn
                    [tooltip]="'Download attendance as PDF' | translate"
                    (clicked)="exportActivityPdf()">
                    <fa-duotone-icon
                      class="header-action-icon"
                      [icon]="faFilePdf"></fa-duotone-icon>
                  </andor-square-icon-btn>
                  <andor-square-icon-btn
                    [tooltip]="'Download attendance as Markdown' | translate"
                    (clicked)="exportActivityMarkdown()">
                    <fa-icon class="header-action-icon" [icon]="faMarkdown"></fa-icon>
                  </andor-square-icon-btn>
                </div>
              }
            </div>
          }
          @switch (tab) {
            @case ('breakout') {
              <div class="breakout-content">
                <andor-text-field
                  [errorMessage]="
                    'The name must contain at least 3 letters and not exceed 100 letters.'
                      | translate
                  "
                  [placeholder]="'Your Breakout Room' | translate"
                  [formControl]="breakoutRoomNameControl"
                  [trailingButton]="breakoutRoomTrailingButton"></andor-text-field>
              </div>
            }
            @case ('activity') {
              <andor-activity-log></andor-activity-log>
            }
            @case ('transcription') {
              @let canRecord = (permissionStore.startRecording$ | async) ?? false;
              <div class="transcription-content">
                <!-- Message log (top) — grows into the space freed by hiding the
                     info note while recording. -->
                <div class="transcription-log">
                  @if (activeTranscript().length > 0) {
                    <div #transcriptList class="transcript-list">
                      @for (line of activeTranscript(); track line.id) {
                        <div class="transcript-line">
                          <div class="transcript-line-head">
                            <span class="transcript-sender">{{ line.senderName }}</span>
                            <span class="transcript-time">
                              {{ line.timestamp | localizedDate: 'shortTime' }}
                            </span>
                          </div>
                          <div class="transcript-content">{{ line.content }}</div>
                        </div>
                      }
                    </div>
                    <!-- After a stop: the server deletes the transcript after the
                         retention window; the copy in this browser stays. -->
                    @if (transcriptRetention(); as retention) {
                      <andor-notice
                        class="transcript-retention"
                        role="status"
                        [variant]="retention.remaining ? 'warning' : 'info'">
                        @if (retention.remaining; as remaining) {
                          {{
                            'This transcript will be deleted from the server in [time]. Download it to keep a copy.'
                              | translate: { time: remaining }
                          }}
                        } @else {
                          {{
                            'The transcript was deleted from the server. This browser still keeps its copy.'
                              | translate
                          }}
                        }
                      </andor-notice>
                    }
                    <button
                      class="transcription-btn secondary"
                      [disabled]="isDownloadingTranscript()"
                      (click)="downloadTranscript()">
                      @if (isDownloadingTranscript()) {
                        <fa-icon class="recording-spin" [icon]="faSpinnerThird"></fa-icon>
                      } @else {
                        <fa-icon [icon]="faDownload"></fa-icon>
                      }
                      <span>{{ 'Download Transcript' | translate }}</span>
                    </button>
                  } @else if (isRecording()) {
                    <div class="transcript-empty">
                      {{ 'Waiting for transcription…' | translate }}
                    </div>
                  }
                </div>

                <!-- Divider between the log and the controls below. -->
                @if (activeTranscript().length > 0 || isRecording()) {
                  <hr class="transcript-separator" />
                }

                <!-- Controls (bottom) — the info note only shows before recording. -->
                <div class="transcription-controls">
                  @if (!isRecording()) {
                    <div class="transcription-info-box" role="note">
                      {{
                        'Starting AI Transcription will record the audio of this room and may send it to third-party providers for processing.'
                          | translate
                      }}
                    </div>
                  }
                  @if (isRecording()) {
                    <!-- Live indicator while a transcription runs. Anyone holding
                         the recording permission gets the Stop button inline (any
                         permitted user can stop it, not just the starter); users
                         without it just see who started it. -->
                    <div class="transcription-running" role="status" aria-live="polite">
                      <div class="transcription-wave" aria-hidden="true">
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      @if (canRecord) {
                        <andor-square-icon-btn
                          class="transcription-record-btn transcription-stop-btn"
                          variant="danger"
                          [disabled]="appService.maintenance.active() || isTogglingRecording()"
                          (clicked)="toggleRecording()">
                          @if (isTogglingRecording()) {
                            <fa-icon class="recording-spin" [icon]="faSpinnerThird"></fa-icon>
                            <span>{{ 'Stopping recording…' | translate }}</span>
                          } @else {
                            <span>{{ 'Stop Recording' | translate }}</span>
                          }
                        </andor-square-icon-btn>
                      } @else {
                        <div class="transcription-running-text">
                          <span class="transcription-running-label">
                            {{ 'Transcription in progress' | translate }}
                          </span>
                          <span class="transcription-running-by">
                            {{ 'Recording started by' | translate: { name: recordingOwnerName() } }}
                          </span>
                        </div>
                      }
                    </div>
                  } @else {
                    <andor-square-icon-btn
                      class="transcription-record-btn"
                      variant="success"
                      [disabled]="
                        appService.maintenance.active() || !canRecord || isTogglingRecording()
                      "
                      [tooltip]="
                        !canRecord
                          ? ('You don\'t have permission to start recording.' | translate)
                          : ''
                      "
                      tooltipPosition="above"
                      (clicked)="toggleRecording()">
                      @if (isTogglingRecording()) {
                        <fa-icon class="recording-spin" [icon]="faSpinnerThird"></fa-icon>
                        <span>{{ 'Starting recording…' | translate }}</span>
                      } @else {
                        <span>{{ 'Start Recording' | translate }}</span>
                      }
                    </andor-square-icon-btn>
                  }
                </div>
              </div>
            }
          }
        </div>
        <div class="action-buttons">
          @if (sidePanelClass() === '') {
            <andor-icon-btn (click)="openSidePanel()">
              <fa-duotone-icon
                [icon]="faBars"
                [andorTooltip]="'Open Side Panel' | translate"></fa-duotone-icon>
            </andor-icon-btn>
          } @else {
            <andor-square-icon-btn
              [variant]="tab === 'breakout' ? 'active' : 'default'"
              [tooltip]="'Create Breakout Room' | translate"
              (clicked)="selectTab('breakout')">
              <fa-duotone-icon [icon]="faGrid2Plus"></fa-duotone-icon>
            </andor-square-icon-btn>
            <andor-square-icon-btn
              [variant]="tab === 'activity' ? 'active' : 'default'"
              [tooltip]="'Activity log' | translate"
              (clicked)="selectTab('activity')">
              <fa-duotone-icon [icon]="faListCheck"></fa-duotone-icon>
            </andor-square-icon-btn>
            <andor-square-icon-btn
              [variant]="tab === 'transcription' ? 'active' : 'default'"
              [tooltip]="
                odinService.transcriptionBot()
                  ? ('Session is being recorded and transcribed' | translate)
                  : ('AI Transcription' | translate)
              "
              (clicked)="selectTab('transcription')">
              <span class="ai-icon-wrap">
                <fa-duotone-icon [icon]="faMicrochipAi"></fa-duotone-icon>
                @if (odinService.transcriptionBot()) {
                  <span class="transcription-recording-dot" aria-hidden="true"></span>
                }
              </span>
            </andor-square-icon-btn>
            <andor-square-icon-btn
              [tooltip]="'Close Side Panel' | translate"
              (clicked)="closeSidePanel()">
              <fa-duotone-icon [icon]="faArrowRightFromLine"></fa-duotone-icon>
            </andor-square-icon-btn>
          }
        </div>
      </div>
    </div>
  </div>
</div>
